import { jwtDecode } from "jwt-decode";

interface JWTPayload {
  sub: string;
  // Add other JWT claims if needed
}

export const getTenantId = (): string => {
  const token = localStorage.getItem("authToken");
  if (!token) return "";
  try {
    const decoded = jwtDecode<JWTPayload>(token);
    return decoded.sub;
  } catch (error) {
    console.error("Error decoding JWT:", error);
    return "";
  }
};

// Google Ads Authentication Management
interface GoogleAdsAuthData {
  userId: string;
  accessToken?: string;
  refreshToken?: string;
  expiresAt?: number;
  isConnected: boolean;
}

const GOOGLE_ADS_AUTH_KEY = "google_ads_auth";

export const GoogleAdsAuthManager = {
  // Save authentication data to localStorage
  saveAuthData(authData: GoogleAdsAuthData): void {
    try {
      console.log("Saving Google Ads auth data:", authData);
      localStorage.setItem(GOOGLE_ADS_AUTH_KEY, JSON.stringify(authData));
    } catch (error) {
      console.error("Failed to save Google Ads auth data:", error);
    }
  },

  // Get authentication data from localStorage
  getAuthData(): GoogleAdsAuthData | null {
    try {
      const stored = localStorage.getItem(GOOGLE_ADS_AUTH_KEY);
      if (!stored) return null;

      const authData = JSON.parse(stored);

      // Check if token is expired
      if (authData.expiresAt && authData.expiresAt < Date.now()) {
        this.clearAuthData();
        return null;
      }

      return authData;
    } catch (error) {
      console.error("Failed to get Google Ads auth data:", error);
      return null;
    }
  },

  // Clear authentication data from localStorage
  clearAuthData(): void {
    try {
      localStorage.removeItem(GOOGLE_ADS_AUTH_KEY);
    } catch (error) {
      console.error("Failed to clear Google Ads auth data:", error);
    }
  },

  // Check if user is authenticated
  isAuthenticated(): boolean {
    const authData = this.getAuthData();
    return authData !== null && authData.isConnected;
  },

  // Get current user ID
  getUserId(): string {
    const authData = this.getAuthData();
    console.log("User ID:", authData?.userId);
    return authData?.userId || "default";
  },

  // Get access token
  getAccessToken(): string | null {
    const authData = this.getAuthData();
    return authData?.accessToken || null;
  },

  // Update connection status
  updateConnectionStatus(isConnected: boolean): void {
    const authData = this.getAuthData();
    if (authData) {
      authData.isConnected = isConnected;
      this.saveAuthData(authData);
    } else if (isConnected) {
      // Create new auth data if connecting for the first time
      this.saveAuthData({
        userId: this.generateUserId(),
        isConnected: true,
      });
    }
  },

  // Generate a unique user ID
  generateUserId(): string {
    const timestamp = Date.now().toString(36);
    const randomStr = Math.random().toString(36).substring(2, 15);
    return `user_${timestamp}_${randomStr}`;
  },

  // Initialize auth data with a generated user ID
  initializeAuthData(): void {
    const existingData = this.getAuthData();
    if (!existingData) {
      this.saveAuthData({
        userId: this.generateUserId(),
        isConnected: false,
      });
    }
  },
};
